import { useEffect, useCallback, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

// Chakra
import { Box, Text, Button, useDisclosure } from '@chakra-ui/react';

import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import { GiveawaysActiveIcon } from 'components/Icons/Icons';
import { apiService } from 'services/ApiService';

import GiveawayPrizes from './components/GiveawayPrizes';
import GiveawayWinners from './components/GiveawayWinners';
import GenerationResultModal from './components/GenerationResultModal';
import NotEnoughWinnersModal from './components/NotEnoughWinnersModal';
import GiveawaysLogs from './components/GiveawaysLogs';
import GiveawayEntries from './components/GiveawayEntries';

export default function GiveawaysDetail() {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const [
    generateWinnersButtonEnabled,
    setGenerateWinnersButtonEnabled,
  ] = useState(true);
  const [generateWinnersLoading, setGenerateWinnersLoading] = useState(null);
  const [finishButtonEnabled, setFinishButtonEnabled] = useState(true);
  const [hardShowWinners, setHardShowWinners] = useState(true);

  const { data: giveaway, isLoading } = useQuery({
    queryKey: ['giveaway'],
    queryFn: async () => {
      const { data } = await apiService.getGiveaway({ id });

      return data;
    },
  });

  const {
    data: winners,
    isLoading: isWinnersLoading,
    refetch: refetchWinners,
  } = useQuery({
    refetchInterval: 10000,
    queryKey: ['giveawayWinners'],
    queryFn: async () => {
      const { data } = await apiService.getGiveawayWinners({ id });
      return data;
    },
  });

  useEffect(() => {
    if (!winners) {
      return;
    }
    console.log(winners.isWinnersGenerated);
    if (winners.status === 'active') {
      setFinishButtonEnabled(true);
    } else {
      setFinishButtonEnabled(false);
    }
    // setGenerateWinnersButtonEnabled(!winners.isWinnersGenerated);
  }, [winners]);

  const [generationTime, setGenerationTime] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const generateWinners = useCallback(
    async ({ test }) => {
      setGenerateWinnersButtonEnabled(false);
      setGenerateWinnersLoading(test);
      try {
        const { data } = await apiService.generateWinners({
          giveawayId: id,
          test,
        });
        setGenerationTime(data?.generationTime);
        await refetchWinners();
      } finally {
        setGenerateWinnersButtonEnabled(true);
        setGenerateWinnersLoading(null);
      }
      queryClient.invalidateQueries(['giveawayLogs']);
      onOpen();
    },
    [id, refetchWinners]
  );

  const finishGiveaway = useCallback(async () => {
    if (!winners) {
      return;
    }
    const innerWinners = winners.winners;
    const prizes = winners.prizes;
    if (prizes.length !== innerWinners.length) {
      alert('Not all winners have been drawn yet');
      return;
    }
    await apiService.finishGiveaway({ giveawayId: id });
    queryClient.invalidateQueries(['giveaway']);
    await refetchWinners();
    setGenerateWinnersButtonEnabled(false);
  }, [id, queryClient, refetchWinners, winners]);

  const {
    isOpen: isNotEnoughWinnersModalOpen,
    onOpen: onNotEnoughWinnersModalOpen,
    onClose: onNotEnoughWinnersModalClose,
  } = useDisclosure();

  const goTo = useCallback(
    (theme) => {
      // history.push(`/game/${theme}/${id}`);
      window.open(`/#/game/${theme}/${id}`, '_blank');
    },
    [id]
  );

  const getUrl = useCallback(() => {
    if (window.location.hash.includes('daily-giveaways')) {
      return '#/daily-giveaways';
    }
    return '#/giveaways';
  }, []);

  return (
    <>
      <Box zIndex="9" position="relative">
        {!isLoading && giveaway && (
          <>
            <Box position="relative">
              <Breadcrumbs
                icon={GiveawaysActiveIcon}
                target="Giveaways"
                url={getUrl()}
                current={`${giveaway.title}`}
              />

              <Button
                onClick={() => finishGiveaway()}
                disabled={!finishButtonEnabled}
                variant="submit-md-delete"
                mb="34px"
                position="absolute"
                top={0}
                right={0}
                w={250}
              >
                Finish Giveaway
              </Button>
            </Box>

            {giveaway?.declareSetting !== 'auto' && (
              <Box display="flex" gap={4} px={48}>
                <Button
                  onClick={() => generateWinners({ test: true })}
                  disabled={!generateWinnersButtonEnabled}
                  variant="warning"
                  mb="34px"
                  isLoading={generateWinnersLoading === true}
                  className={!generateWinnersButtonEnabled ? 'freeze' : ''}
                  loadingText="Generating..."
                >
                  Generate All Test Winners
                </Button>
                <Button
                  onClick={() => generateWinners({ test: false })}
                  disabled={!generateWinnersButtonEnabled}
                  variant="submit"
                  mb="34px"
                  isLoading={generateWinnersLoading === false}
                  className={!generateWinnersButtonEnabled ? 'freeze' : ''}
                  loadingText="Generating..."
                >
                  Generate All Winners
                </Button>
                <Button
                  onClick={() => {
                    winners?.winners?.length < winners?.prizes?.length
                      ? onNotEnoughWinnersModalOpen()
                      : goTo('slot-machine');
                  }}
                  variant="submit"
                  loadingText="Theme Two"
                  mb="34px"
                >
                  Reveal Winners
                </Button>
              </Box>
            )}
            {winners && (
              <>
                <Text
                  variant="title-lg"
                  mt="40px"
                  mb="14px"
                  onClick={() => setHardShowWinners(!hardShowWinners)}
                >
                  Winners
                </Text>
                <GiveawayWinners
                  isEditable={giveaway?.declareSetting !== 'auto'}
                  hardShowWinners={hardShowWinners}
                  giveaway={winners}
                />
              </>
            )}
            <Text variant="title-lg" mt="40px" mb="14px">
              Prizes
            </Text>
            <GiveawayPrizes giveaway={giveaway} />
            {/* Giveaway ID for auditing, placed near Entries */}
            <Text
              variant="text-md"
              fontWeight="bold"
              mt="40px"
              mb="2"
              color="white"
            >
              Giveaway ID: {giveaway.id}
            </Text>
            <Text variant="title-lg" mt="40px" mb="14px">
              Entries
            </Text>
            <GiveawayEntries giveawayId={giveaway.id} />
          </>
        )}

        {giveaway?.declareSetting !== 'auto' && (
          <GiveawaysLogs giveawayId={id} />
        )}
      </Box>
      <GenerationResultModal
        isOpen={isOpen}
        onClose={onClose}
        generationTime={generationTime}
      />
      <NotEnoughWinnersModal
        isOpen={isNotEnoughWinnersModalOpen}
        onClose={onNotEnoughWinnersModalClose}
      />
    </>
  );
}
